import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Trash2, Pencil, Radio, Copy, Check } from "lucide-react";

interface Postback {
  id: number;
  offerId: number;
  deliveryMethod: string;
  pixelLevel: string;
  status: string;
  pixelType: string;
  postbackUrl: string;
  htmlCode: string;
  delayMs: number;
  createdAt: string;
}

const MACROS = [
  { name: "{transaction_id}", desc: "Click transaction ID" },
  { name: "{event_id}", desc: "Conversion event ID" },
  { name: "{offer_id}", desc: "Offer ID" },
  { name: "{affiliate_id}", desc: "Affiliate ID" },
  { name: "{payout}", desc: "Payout amount" },
  { name: "{revenue}", desc: "Revenue amount" },
  { name: "{sale_amount}", desc: "Sale amount" },
  { name: "{sub1}", desc: "Sub ID 1" },
  { name: "{sub2}", desc: "Sub ID 2" },
  { name: "{sub3}", desc: "Sub ID 3" },
  { name: "{sub4}", desc: "Sub ID 4" },
  { name: "{sub5}", desc: "Sub ID 5" },
  { name: "{source_id}", desc: "Source ID" },
  { name: "{country_code}", desc: "Country code" },
];

export function AffiliatePostbacksTab({ affiliateId }: { affiliateId: string }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingPb, setEditingPb] = useState<Postback | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["affiliates", affiliateId, "postbacks"],
    queryFn: async () => {
      const res = await api.get(`/v1/affiliates/${affiliateId}/postbacks`);
      if (!res.ok) throw new Error("Failed to fetch postbacks");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (pbId: number) => {
      const res = await api.delete(`/v1/affiliates/${affiliateId}/postbacks/${pbId}`);
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["affiliates", affiliateId, "postbacks"] }),
  });

  const postbacks: Postback[] = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700">{postbacks.length} postback{postbacks.length !== 1 ? "s" : ""}</h3>
        <button onClick={() => { setEditingPb(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800">
          <Plus className="w-4 h-4" /> Add Postback
        </button>
      </div>

      {isLoading ? (
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
      ) : postbacks.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
          <Radio className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No postbacks configured</p>
          <p className="text-xs text-gray-400 mt-1">Add a postback URL to fire on conversions</p>
        </div>
      ) : (
        <div className="space-y-3">
          {postbacks.map((pb) => (
            <div key={pb.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <StatusBadge status={pb.status} />
                  <span className="text-xs font-medium text-gray-500 uppercase">{pb.pixelLevel}</span>
                  <span className="text-xs text-gray-400">|</span>
                  <span className="text-xs font-medium text-gray-500 uppercase">{pb.pixelType}</span>
                  {pb.offerId > 0 && (
                    <>
                      <span className="text-xs text-gray-400">|</span>
                      <span className="text-xs text-gray-500">Offer #{pb.offerId}</span>
                    </>
                  )}
                  {pb.delayMs > 0 && (
                    <>
                      <span className="text-xs text-gray-400">|</span>
                      <span className="text-xs text-gray-500">{pb.delayMs}ms delay</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => { setEditingPb(pb); setShowForm(true); }}
                    className="p-1.5 text-gray-400 hover:text-gray-600 rounded"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => { if (window.confirm("Delete this postback?")) deleteMutation.mutate(pb.id); }}
                    className="p-1.5 text-red-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {pb.postbackUrl && (
                <div className="bg-gray-50 rounded-lg px-3 py-2 mt-2">
                  <code className="text-xs text-gray-700 break-all">{pb.postbackUrl}</code>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <PostbackFormModal
          affiliateId={affiliateId}
          postback={editingPb}
          onClose={() => { setShowForm(false); setEditingPb(null); }}
        />
      )}
    </div>
  );
}

function PostbackFormModal({ affiliateId, postback, onClose }: {
  affiliateId: string; postback: Postback | null; onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!postback;

  const [pixelLevel, setPixelLevel] = useState(postback?.pixelLevel ?? "global");
  const [pixelType, setPixelType] = useState(postback?.pixelType ?? "conversion");
  const [offerId, setOfferId] = useState(String(postback?.offerId ?? 0));
  const [postbackUrl, setPostbackUrl] = useState(postback?.postbackUrl ?? "");
  const [delayMs, setDelayMs] = useState(String(postback?.delayMs ?? 0));
  const [status, setStatus] = useState(postback?.status ?? "active");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedMacro, setCopiedMacro] = useState("");

  function insertMacro(macro: string) {
    setPostbackUrl((prev) => prev + macro);
  }

  function copyMacro(macro: string) {
    navigator.clipboard.writeText(macro);
    setCopiedMacro(macro);
    setTimeout(() => setCopiedMacro(""), 1500);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        pixelLevel,
        pixelType,
        offerId: pixelLevel === "specific" ? Number(offerId) : 0,
        postbackUrl,
        deliveryMethod: "postback",
        delayMs: Number(delayMs),
        status,
      };
      const res = isEdit
        ? await api.patch(`/v1/affiliates/${affiliateId}/postbacks/${postback!.id}`, payload)
        : await api.post(`/v1/affiliates/${affiliateId}/postbacks`, payload);
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Failed"); }
      queryClient.invalidateQueries({ queryKey: ["affiliates", affiliateId, "postbacks"] });
      onClose();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">{isEdit ? "Edit" : "Add"} Postback</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pixel Level</label>
              <select value={pixelLevel} onChange={(e) => setPixelLevel(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                <option value="global">Global (all offers)</option>
                <option value="specific">Specific offer</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pixel Type</label>
              <select value={pixelType} onChange={(e) => setPixelType(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                <option value="conversion">Conversion</option>
                <option value="post_conversion">Post-conversion</option>
              </select>
            </div>
          </div>

          {pixelLevel === "specific" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Offer ID</label>
              <input type="number" min="1" value={offerId} onChange={(e) => setOfferId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Enter offer ID" />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Postback URL *</label>
            <textarea required value={postbackUrl} onChange={(e) => setPostbackUrl(e.target.value)} rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-900"
              placeholder="https://example.com/postback?tid={transaction_id}&payout={payout}" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Available Macros (click to insert)</label>
            <div className="flex flex-wrap gap-1">
              {MACROS.map((m) => (
                <button key={m.name} type="button"
                  onClick={() => insertMacro(m.name)}
                  onDoubleClick={() => copyMacro(m.name)}
                  className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200"
                  title={m.desc}>
                  {copiedMacro === m.name ? <Check className="w-3 h-3 inline text-green-600" /> : null}
                  {m.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delay (ms)</label>
              <input type="number" min="0" max="300000" value={delayMs} onChange={(e) => setDelayMs(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {loading ? "Saving..." : isEdit ? "Save Changes" : "Add Postback"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
